import i18next from 'i18next';
import {submitPriceUpdate} from './workspace-api';
import {withButtonPending} from '../../common/js/button-pending';
import {bindDateTimePickerEvents, dateTimePickerHtml, isDateTimePickerValueInPast} from '../../common/js/date-time-picker';
import {
    createPostingOperationAttemptTracker,
    getPostingOperationErrorMessage,
} from './posting-operation-attempt';

let modalConfig = {
    findAdvert: null,
    onSuccess: null,
    onReport: null,
    displayError: null,
    onRequestError: null,
};
let isBound = false;
const singlePriceUpdateAttempt = createPostingOperationAttemptTracker({
    storageKey: 'adsPostingOperationAttempt:single-price:v1',
});

export const priceEditIconHtml = (className = 'inventory-price-edit-icon') => `
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill-rule="evenodd" class="${className}" aria-hidden="true" focusable="false">
        <path d="M22.099 6.429c-2.126 2.126-7.116 7.115-8.333 8.328a2.756 2.756 0 0 1-.912.604c-.976.405-3.357 1.339-4.057 1.386a1.45 1.45 0 0 1-1.544-1.544c.047-.7.981-3.079 1.379-4.051.139-.345.346-.658.609-.92l8.33-8.331a2.153 2.153 0 0 1 3.042 0l1.486 1.486a2.153 2.153 0 0 1 0 3.042zM8.756 15.244c.684-.086 2.683-.92 3.523-1.269a1.249 1.249 0 0 0 .426-.279l.001-.001a8610.44 8610.44 0 0 0 8.332-8.327.65.65 0 0 0 0-.92l-1.486-1.486a.65.65 0 0 0-.92 0l-8.331 8.331a1.24 1.24 0 0 0-.277.419l-.002.004c-.345.842-1.18 2.843-1.266 3.528z" fill="currentColor"></path>
        <path d="M20.118 7.349a.75.75 0 1 1-1.061 1.061L15.59 4.943a.75.75 0 1 1 1.061-1.061zM13.997 13.47a.749.749 0 1 1-1.06 1.06L9.47 11.063a.749.749 0 1 1 1.06-1.06z" fill="currentColor"></path>
        <path d="M8.5 3.25a.75.75 0 0 1 0 1.5H5A2.25 2.25 0 0 0 2.75 7v12A2.25 2.25 0 0 0 5 21.25h13A2.25 2.25 0 0 0 20.25 19v-3.5a.75.75 0 0 1 1.5 0V19A3.752 3.752 0 0 1 18 22.75H5A3.752 3.752 0 0 1 1.25 19V7A3.75 3.75 0 0 1 5 3.25z" fill="currentColor"></path>
    </svg>`;

const escapeHtml = (value = '') => String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');

export const priceUpdateSelectHtml = ({id, label, options = [], selectedValue = '', className = '', inputClass = ''}) => {
    const selected = options.find((option) => String(option.value) === String(selectedValue)) || options[0] || {value: '', label: ''};
    const optionHtml = options.map((option, index) => {
        const isSelected = String(option.value) === String(selected.value);
        return `
            <li
                id="${id}-option-${index}"
                class="ogo-select__option ${isSelected ? 'is-selected' : ''}"
                role="option"
                aria-selected="${isSelected ? 'true' : 'false'}"
                data-ogo-select-option
                data-select-value="${escapeHtml(option.value)}"
                data-select-label="${escapeHtml(option.label)}"
            >
                ${escapeHtml(option.label)}
            </li>`;
    }).join('');

    return `
        <div class="ogo-field price-update-select-field ${className}">
            <label class="ogo-field-label" for="${id}-toggle">${escapeHtml(label)}</label>
            <div class="ogo-select ogo-select--full" data-ogo-select>
                <button
                    type="button"
                    id="${id}-toggle"
                    class="ogo-select__toggle"
                    aria-haspopup="listbox"
                    aria-expanded="false"
                    aria-controls="${id}-dropdown"
                >
                    <span class="ogo-select__label">${escapeHtml(selected.label)}</span>
                    <span class="ogo-select__arrow" aria-hidden="true"></span>
                </button>
                <div class="ogo-select__dropdown" role="presentation">
                    <ul class="ogo-select__options" role="listbox" id="${id}-dropdown">
                        ${optionHtml}
                    </ul>
                </div>
                <input type="hidden" id="${id}" class="ogo-select__value ${inputClass}" value="${escapeHtml(selected.value)}">
            </div>
            <div class="invalid-feedback"></div>
        </div>`;
};

export const setPriceUpdateSelectValue = (selector, value) => {
    const input = $(selector);
    const select = input.closest('[data-ogo-select]');
    const option = select.find(`[data-ogo-select-option][data-select-value="${String(value).replace(/"/g, '\\"')}"]`).first();
    if (!input.length) {
        return;
    }
    input.val(value).trigger('change');
    if (!select.length || !option.length) {
        return;
    }
    select.find('.ogo-select__label').text(option.data('select-label') || option.text().trim());
    select.find('[data-ogo-select-option]').removeClass('is-selected').attr('aria-selected', 'false');
    option.addClass('is-selected').attr('aria-selected', 'true');
};

const scheduledDatePickerHtml = (id = 'price-update-scheduled-at') => dateTimePickerHtml({
    id,
    label: i18next.t('ads:priceUpdate.scheduledAt', {defaultValue: 'Scheduled time'}),
    placeholder: i18next.t('ads:priceUpdate.selectDateTime', {defaultValue: 'Select date and time'}),
    className: 'price-update-scheduled-row d-none',
    inputClass: 'price-update-scheduled-at',
    disablePast: true,
    labels: {
        today: i18next.t('common:today', {defaultValue: 'Today'}),
        clear: i18next.t('common:clear', {defaultValue: 'Clear'}),
        done: i18next.t('common:done', {defaultValue: 'Done'}),
        previousMonth: i18next.t('common:previousMonth', {defaultValue: 'Previous month'}),
        nextMonth: i18next.t('common:nextMonth', {defaultValue: 'Next month'}),
        time: i18next.t('common:time', {defaultValue: 'Time'}),
        hour: i18next.t('common:hour', {defaultValue: 'Hour'}),
        minute: i18next.t('common:minute', {defaultValue: 'Minute'}),
    },
});

const executionFieldsHtml = (prefix) => priceUpdateSelectHtml({
    id: `${prefix}-execution-mode`,
    label: i18next.t('ads:priceUpdate.executionMode', {defaultValue: 'When to apply'}),
    selectedValue: 'now',
    inputClass: 'price-update-execution-mode',
    options: [
        {value: 'now', label: i18next.t('ads:priceUpdate.executeNow', {defaultValue: 'Now'})},
        {value: 'scheduled', label: i18next.t('ads:priceUpdate.executeScheduled', {defaultValue: 'Schedule'})},
    ],
}) + scheduledDatePickerHtml(`${prefix}-scheduled-at`);

const priceSegmentedFieldHtml = ({id, label, options = [], selectedValue = ''}) => {
    const selected = String(selectedValue || (options[0] && options[0].value) || '');
    const buttonsHtml = options.map((option) => {
        const isSelected = String(option.value) === selected;
        return `
            <button
                type="button"
                class="price-update-segment-button ${isSelected ? 'is-active' : ''}"
                data-price-segment-target="${id}"
                data-price-segment-value="${escapeHtml(option.value)}"
                aria-pressed="${isSelected ? 'true' : 'false'}"
            >
                ${escapeHtml(option.label)}
            </button>`;
    }).join('');
    return `
        <div class="ogo-field price-update-segment-field">
            <label class="ogo-field-label">${escapeHtml(label)}</label>
            <div class="price-update-segment price-update-segment--wide" role="group" aria-label="${escapeHtml(label)}">
                ${buttonsHtml}
            </div>
            <input type="hidden" id="${id}" value="${escapeHtml(selected)}">
        </div>`;
};

const singleExecutionFieldsHtml = () => priceSegmentedFieldHtml({
    id: 'price-update-execution-mode',
    label: i18next.t('ads:priceUpdate.executionMode', {defaultValue: 'When to apply'}),
    selectedValue: 'now',
    options: [
        {value: 'now', label: i18next.t('ads:priceUpdate.executeNow', {defaultValue: 'Now'})},
        {value: 'scheduled', label: i18next.t('ads:priceUpdate.executeScheduled', {defaultValue: 'Schedule'})},
    ],
}) + scheduledDatePickerHtml();

export const priceUpdateSingleModalHtml = () => `
    <div class="modal fade" id="price-update-modal" tabindex="-1" aria-labelledby="price-update-title" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered">
            <div class="modal-content price-update-dialog">
                <div class="modal-header">
                    <h5 class="modal-title" id="price-update-title">${i18next.t('ads:priceUpdate.singleTitle', {defaultValue: 'Change price'})}</h5>
                    <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="${i18next.t('ads:workspace.close', {defaultValue: 'Close'})}"></button>
                </div>
                <div class="modal-body">
                    <form id="price-update-form" class="price-update-form">
                        <input type="hidden" id="price-update-account-id">
                        <input type="hidden" id="price-update-ad-id">
                        <input type="hidden" id="price-update-currency">
                        ${priceSegmentedFieldHtml({
                            id: 'price-update-mode',
                            label: i18next.t('ads:priceUpdate.mode', {defaultValue: 'Price mode'}),
                            selectedValue: 'price',
                            options: [
                                {value: 'price', label: i18next.t('ads:priceUpdate.modePrice', {defaultValue: 'Price'})},
                                {value: 'free', label: i18next.t('ads:priceUpdate.modeFree', {defaultValue: 'Free'})},
                                {value: 'trade', label: i18next.t('ads:priceUpdate.modeTrade', {defaultValue: 'Exchange'})},
                            ],
                        })}
                        <div class="price-update-helper price-update-value-row">
                            <div class="price-update-helper-title">${i18next.t('ads:priceUpdate.quickAdjust', {defaultValue: 'Quick adjustment'})}</div>
                            <div class="price-update-helper-grid">
                                <input type="hidden" id="price-update-adjustment-direction" value="increase">
                                <input type="hidden" id="price-update-adjustment-kind" value="amount">
                                <div class="price-update-segment" role="group" aria-label="${i18next.t('ads:priceUpdate.operation', {defaultValue: 'Operation'})}">
                                    <button type="button" class="price-update-segment-button is-active" data-price-adjust-direction="increase" aria-pressed="true">+</button>
                                    <button type="button" class="price-update-segment-button" data-price-adjust-direction="decrease" aria-pressed="false">−</button>
                                </div>
                                <div class="price-update-segment" role="group" aria-label="${i18next.t('ads:priceUpdate.adjustmentType', {defaultValue: 'Adjustment type'})}">
                                    <button type="button" class="price-update-segment-button is-active" data-price-adjust-kind="amount" aria-pressed="true">123</button>
                                    <button type="button" class="price-update-segment-button" data-price-adjust-kind="percent" aria-pressed="false">%</button>
                                </div>
                                <div class="ogo-field price-update-helper-value-field">
                                    <label class="visually-hidden" for="price-update-adjustment-value">${i18next.t('ads:priceUpdate.adjustmentValue', {defaultValue: 'Value'})}</label>
                                    <input id="price-update-adjustment-value" class="form-control og-input" type="number" min="0.01" step="0.01">
                                    <div class="invalid-feedback"></div>
                                </div>
                                <button type="button" id="price-update-adjustment-apply" class="outline-button price-update-helper-apply">
                                    =
                                </button>
                            </div>
                        </div>
                        <div class="ogo-field price-update-value-row price-update-price-field">
                            <label class="ogo-field-label" for="price-update-value">${i18next.t('ads:workspace.priceField', {defaultValue: 'Price'})}</label>
                            <input id="price-update-value" class="form-control og-input" type="number" min="0" step="0.01">
                            <div class="invalid-feedback"></div>
                        </div>
                        <label class="price-update-toggle-row" for="price-update-negotiable">
                            <span>${i18next.t('ads:draftEditor.priceNegotiable', {defaultValue: 'Negotiable'})}</span>
                            <label class="switch">
                                <input id="price-update-negotiable" type="checkbox">
                                <span class="slider round"></span>
                            </label>
                        </label>
                        ${singleExecutionFieldsHtml()}
                    </form>
                </div>
                <div class="modal-footer workspace-modal-footer">
                    <button type="button" class="outline-button" data-bs-dismiss="modal">${i18next.t('ads:workspace.close', {defaultValue: 'Close'})}</button>
                    <button type="submit" form="price-update-form" class="filled-button">${i18next.t('ads:priceUpdate.save', {defaultValue: 'Save'})}</button>
                </div>
            </div>
        </div>
    </div>`;

export const priceUpdateBulkExecutionFieldsHtml = executionFieldsHtml;

const clearPriceUpdateErrors = () => {
    $('#price-update-modal .is-invalid').removeClass('is-invalid');
    $('#price-update-modal .invalid-feedback').text('');
};

const showPriceUpdateFieldError = (fieldSelector, message) => {
    const field = $(fieldSelector);
    field.addClass('is-invalid');
    const datePicker = field.closest('[data-date-time-picker]');
    if (datePicker.length) {
        datePicker.addClass('is-invalid').find('.invalid-feedback').text(message);
        return;
    }
    const selectField = field.closest('.price-update-select-field');
    const selectFeedback = selectField.find('.invalid-feedback');
    if (selectFeedback.length) {
        selectField.find('.ogo-select').addClass('is-invalid');
        selectFeedback.text(message);
        return;
    }
    field.siblings('.invalid-feedback').text(message);
};

export const resetPriceUpdateDatePicker = (inputSelector) => {
    const input = $(inputSelector);
    if (!input.length) {
        return;
    }
    const picker = input.closest('[data-date-time-picker]');
    input.removeClass('is-invalid').val('').trigger('change');
    if (!picker.length) {
        return;
    }
    picker
        .removeData('view-year')
        .removeData('view-month')
        .removeClass('is-open is-above is-below is-measuring is-invalid');
    picker.find('[data-date-time-picker-trigger]').attr('aria-expanded', 'false');
    picker.find('[data-date-time-picker-label]').text(picker.data('placeholder') || '');
    picker.find('[data-date-time-picker-calendar]').empty();
    picker.find('.invalid-feedback').text('');
};

const displayPriceUpdateServerErrors = (errors) => {
    const result = {
        inlineDisplayed: false,
        requestErrors: [],
    };
    if (!errors || typeof errors !== 'object') {
        return result;
    }
    Object.entries(errors).forEach(([field, messages]) => {
        const message = Array.isArray(messages) ? messages.join(', ') : String(messages || '');
        if (field === 'price.value' || field === 'price') {
            showPriceUpdateFieldError('#price-update-value', message);
            result.inlineDisplayed = true;
        }
        if (field === 'price.currency') {
            result.requestErrors.push(message);
        }
        if (field === 'scheduled_at') {
            showPriceUpdateFieldError('#price-update-scheduled-at', message);
            result.inlineDisplayed = true;
        }
    });
    return result;
};

const formatPriceInputValue = (value) => {
    const rounded = Math.max(0, Math.round((Number(value) + Number.EPSILON) * 100) / 100);
    return Number.isInteger(rounded)
        ? String(rounded)
        : String(rounded.toFixed(2)).replace(/0+$/, '').replace(/\.$/, '');
};

const updateSinglePriceModeVisibility = () => {
    const mode = $('#price-update-mode').val();
    const acceptsValue = mode === 'price';
    $('.price-update-value-row').toggleClass('d-none', !acceptsValue);
    $('#price-update-value').prop('disabled', !acceptsValue);
    $('#price-update-negotiable').prop('disabled', !acceptsValue).prop('checked', acceptsValue && $('#price-update-negotiable').is(':checked'));
};

export const openPriceUpdateModal = (accountId, adId) => {
    const ad = typeof modalConfig.findAdvert === 'function' ? modalConfig.findAdvert(accountId, adId) : null;
    if (!ad) {
        modalConfig.displayError?.(i18next.t('ads:priceUpdate.adNotFound', {defaultValue: 'Could not find selected advert.'}));
        return;
    }
    const price = ad.price || {};
    const value = Number(price.value || 0);
    let mode = 'price';
    if (price.trade) {
        mode = 'trade';
    } else if (price.budget || value === 0) {
        mode = 'free';
    }
    clearPriceUpdateErrors();
    $('#price-update-account-id').val(accountId);
    $('#price-update-ad-id').val(adId);
    setPriceUpdateSegmentFieldValue('price-update-mode', mode);
    $('#price-update-value').val(value > 0 ? value : '');
    $('#price-update-currency').val(price.currency || 'UAH');
    setPriceUpdateSegmentValue('direction', 'increase');
    setPriceUpdateSegmentValue('kind', 'amount');
    $('#price-update-adjustment-value').val('');
    $('#price-update-negotiable').prop('checked', Boolean(price.negotiable));
    setPriceUpdateSegmentFieldValue('price-update-execution-mode', 'now', {triggerChange: false});
    $('#price-update-modal .price-update-scheduled-row').addClass('d-none');
    resetPriceUpdateDatePicker('#price-update-scheduled-at');
    updateSinglePriceModeVisibility();
    $('#price-update-modal').modal('show');
};

const applyPriceAdjustment = () => {
    clearPriceUpdateErrors();
    const currentPrice = Number($('#price-update-value').val());
    const adjustmentValue = Number($('#price-update-adjustment-value').val());
    const direction = $('#price-update-adjustment-direction').val();
    const kind = $('#price-update-adjustment-kind').val();
    if (!Number.isFinite(currentPrice) || currentPrice < 0) {
        showPriceUpdateFieldError('#price-update-value', i18next.t('ads:priceUpdate.valueInvalid', {defaultValue: 'Enter a valid price.'}));
        return;
    }
    if (!Number.isFinite(adjustmentValue) || adjustmentValue <= 0) {
        showPriceUpdateFieldError('#price-update-adjustment-value', i18next.t('ads:priceUpdate.adjustmentInvalid', {defaultValue: 'Enter a value greater than zero.'}));
        return;
    }
    if (kind === 'percent' && adjustmentValue > 500) {
        showPriceUpdateFieldError('#price-update-adjustment-value', i18next.t('ads:priceUpdate.percentTooHigh', {defaultValue: 'Percent cannot be greater than 500.'}));
        return;
    }
    let nextPrice = currentPrice;
    if (direction === 'increase' && kind === 'amount') {
        nextPrice = currentPrice + adjustmentValue;
    } else if (direction === 'decrease' && kind === 'amount') {
        nextPrice = currentPrice - adjustmentValue;
    } else if (direction === 'increase' && kind === 'percent') {
        nextPrice = currentPrice * (1 + adjustmentValue / 100);
    } else if (direction === 'decrease' && kind === 'percent') {
        nextPrice = currentPrice * (1 - adjustmentValue / 100);
    }
    if (!Number.isFinite(nextPrice) || nextPrice < 0) {
        showPriceUpdateFieldError('#price-update-adjustment-value', i18next.t('ads:priceUpdate.adjustmentResultInvalid', {defaultValue: 'Adjustment cannot make price negative.'}));
        return;
    }
    $('#price-update-value').val(formatPriceInputValue(nextPrice)).trigger('input');
};

const setPriceUpdateSegmentValue = (type, value) => {
    const input = $(`#price-update-adjustment-${type}`);
    const buttons = $(`[data-price-adjust-${type}]`);
    input.val(value);
    buttons.each(function () {
        const button = $(this);
        const selected = String(button.data(`price-adjust-${type}`)) === String(value);
        button.toggleClass('is-active', selected).attr('aria-pressed', selected ? 'true' : 'false');
    });
};

const setPriceUpdateSegmentFieldValue = (target, value, {triggerChange = true} = {}) => {
    const input = $(`#${target}`);
    const buttons = $(`[data-price-segment-target="${target}"]`);
    input.val(value);
    if (triggerChange) {
        input.trigger('change');
    }
    buttons.each(function () {
        const button = $(this);
        const selected = String(button.data('price-segment-value')) === String(value);
        button.toggleClass('is-active', selected).attr('aria-pressed', selected ? 'true' : 'false');
    });
};

const buildSinglePriceUpdatePayload = () => {
    const mode = $('#price-update-mode').val();
    const executionMode = $('#price-update-execution-mode').val() || 'now';
    const payload = {
        execution_mode: executionMode,
        operation_type: 'set_price',
        items: [{
            account_id: Number($('#price-update-account-id').val()),
            ad_id: String($('#price-update-ad-id').val() || ''),
        }],
        price: {
            value: mode === 'price' ? Number($('#price-update-value').val()) : 0,
            currency: $('#price-update-currency').val(),
            trade: mode === 'trade',
            negotiable: mode === 'price' && $('#price-update-negotiable').is(':checked'),
            budget: mode === 'free',
        },
    };
    if (executionMode === 'scheduled') {
        const scheduledAt = $('#price-update-scheduled-at').val();
        payload.scheduled_at = scheduledAt ? new Date(scheduledAt).toISOString() : null;
    }
    return payload;
};

const validateSinglePriceUpdatePayload = (payload) => {
    clearPriceUpdateErrors();
    if (!payload.items[0].account_id || !payload.items[0].ad_id) {
        modalConfig.displayError?.(i18next.t('ads:priceUpdate.adNotFound', {defaultValue: 'Could not find selected advert.'}));
        return false;
    }
    if (payload.execution_mode === 'scheduled' && !payload.scheduled_at) {
        showPriceUpdateFieldError('#price-update-scheduled-at', i18next.t('ads:priceUpdate.scheduledAtRequired', {defaultValue: 'Select scheduled time.'}));
        return false;
    }
    if (payload.execution_mode === 'scheduled' && isDateTimePickerValueInPast('#price-update-scheduled-at')) {
        showPriceUpdateFieldError('#price-update-scheduled-at', i18next.t('ads:placementSchedule.pastTime', {defaultValue: 'Scheduled time must be in the future.'}));
        return false;
    }
    if (!payload.price.currency) {
        modalConfig.displayError?.(i18next.t('ads:priceUpdate.currencyRequired', {defaultValue: 'Select a currency.'}));
        return false;
    }
    if (!payload.price.trade && !payload.price.budget && (!Number.isFinite(payload.price.value) || payload.price.value < 0)) {
        showPriceUpdateFieldError('#price-update-value', i18next.t('ads:priceUpdate.valueInvalid', {defaultValue: 'Enter a valid price.'}));
        return false;
    }
    return true;
};

const hidePriceUpdateModal = () => new Promise((resolve) => {
    const element = document.querySelector('#price-update-modal.show')
        || document.querySelector('#price-update-modal');
    if (!element || !element.classList.contains('show')) {
        resolve();
        return;
    }
    const complete = () => resolve();
    element.addEventListener('hidden.bs.modal', complete, {once: true});
    if (window.bootstrap?.Modal) {
        const modal = window.bootstrap.Modal.getOrCreateInstance(element);
        if (modal._isTransitioning) {
            element.addEventListener('shown.bs.modal', () => modal.hide(), {once: true});
        } else {
            modal.hide();
        }
        return;
    }
    $(element).modal('hide');
});

const displayPriceUpdateRequestErrors = (error) => {
    const idempotencyMessage = getPostingOperationErrorMessage(error, i18next.t.bind(i18next));
    if (idempotencyMessage) {
        if (typeof modalConfig.onRequestError === 'function') {
            modalConfig.onRequestError(error);
        } else {
            modalConfig.displayError?.(idempotencyMessage);
        }
        return;
    }
    const classified = displayPriceUpdateServerErrors(error?.errors || error);
    if (classified.requestErrors.length) {
        const requestError = {
            errors: {request: classified.requestErrors},
            message: classified.requestErrors.join(' '),
        };
        if (typeof modalConfig.onRequestError === 'function') {
            modalConfig.onRequestError(requestError);
        } else {
            modalConfig.displayError?.(requestError.message);
        }
        return;
    }
    if (classified.inlineDisplayed) {
        return;
    }
    if (typeof modalConfig.onRequestError === 'function') {
        modalConfig.onRequestError(error);
        return;
    }
    const message = error?.message || (typeof error === 'string' ? error : i18next.t(
        'ads:priceUpdate.failed',
        {defaultValue: 'Price update failed.'},
    ));
    modalConfig.displayError?.(message);
};

const handleSinglePriceUpdateSubmit = async (event) => {
    event.preventDefault();
    const payload = buildSinglePriceUpdatePayload();
    if (!validateSinglePriceUpdatePayload(payload)) {
        return;
    }
    const submitButton = $('#price-update-modal button[type="submit"]');
    const idempotencyKey = singlePriceUpdateAttempt.getKey(payload);
    let response;
    try {
        response = await withButtonPending(
            submitButton,
            () => submitPriceUpdate(payload, idempotencyKey),
        );
    } catch (error) {
        singlePriceUpdateAttempt.handleError(error);
        displayPriceUpdateRequestErrors(error);
        return;
    }
    singlePriceUpdateAttempt.complete();

    await hidePriceUpdateModal();
    for (const callback of [modalConfig.onSuccess, modalConfig.onReport]) {
        if (typeof callback !== 'function') {
            continue;
        }
        try {
            await callback(response, payload);
        } catch (renderError) {
            console.error('Accepted price update feedback rendering failed.', renderError);
        }
    }
};

const closePriceCustomSelects = (except = null) => {
    $('[data-ogo-select]').not(except || []).removeClass('is-open')
        .find('.ogo-select__toggle')
        .attr('aria-expanded', 'false');
};

export const bindPriceUpdateModalEvents = (config = {}) => {
    modalConfig = {...modalConfig, ...config};
    if (isBound) {
        return;
    }
    isBound = true;
    bindDateTimePickerEvents();

    $('body').on('click', '[data-ogo-select] .ogo-select__toggle', function (event) {
        event.preventDefault();
        const select = $(this).closest('[data-ogo-select]');
        const isOpen = select.hasClass('is-open');
        closePriceCustomSelects(select);
        select.toggleClass('is-open', !isOpen);
        $(this).attr('aria-expanded', isOpen ? 'false' : 'true');
    });
    $('body').on('click', '[data-ogo-select] [data-ogo-select-option]', function (event) {
        event.preventDefault();
        const option = $(this);
        const select = option.closest('[data-ogo-select]');
        const input = select.find('.ogo-select__value');
        setPriceUpdateSelectValue(`#${input.attr('id')}`, option.data('select-value'));
        closePriceCustomSelects();
    });
    $('body').on('click', function (event) {
        if (!$(event.target).closest('[data-ogo-select]').length) {
            closePriceCustomSelects();
        }
    });
    $('body').on('change', '#price-update-mode', updateSinglePriceModeVisibility);
    $('body').on('change', '#price-update-execution-mode', function () {
        $('#price-update-modal .price-update-scheduled-row').toggleClass('d-none', $(this).val() !== 'scheduled');
        if ($(this).val() !== 'scheduled') {
            resetPriceUpdateDatePicker('#price-update-scheduled-at');
        }
    });
    $('body').on('click', '[data-price-segment-target]', function () {
        setPriceUpdateSegmentFieldValue($(this).data('price-segment-target'), $(this).data('price-segment-value'));
    });
    $('body').on('click', '[data-price-adjust-direction]', function () {
        setPriceUpdateSegmentValue('direction', $(this).data('price-adjust-direction'));
    });
    $('body').on('click', '[data-price-adjust-kind]', function () {
        setPriceUpdateSegmentValue('kind', $(this).data('price-adjust-kind'));
    });
    $('body').on('click', '#price-update-adjustment-apply', applyPriceAdjustment);
    $('body').on('submit', '#price-update-form', handleSinglePriceUpdateSubmit);
};
